import { create } from 'zustand';
import type {
  EDAResponse,
  FeatureSelection,
  ProfilesResponse,
  Strategy,
  QualTheme,
  ChatResponse,
  ChartAuditContext,
  ChartAuditOverlaysResponse,
} from '../api/client';

export interface QualThemeAlignment {
  taxonomy_id: string;
  theme_label?: string;
  relevance?: string;
  gate_decision?: string;
  sample_sufficiency?: string;
  quant_agreement?: string;
  reasoning?: string;
  feature_categories?: string[];
  [key: string]: unknown;
}

export interface QualAlignment {
  overall_qual_relevance?: string;
  objective_refinement?: string;
  suggested_objective_text?: string;
  key_evidence?: string[];
  weight_impact_preview?: Record<string, string>;
  suggestion_confidence?: string;
  gate_summary?: Record<string, number>;
  theme_alignment?: QualThemeAlignment[];
  [key: string]: unknown;
}

export interface QualFingerprintFeature {
  direction: string;
  confidence?: string;
}

export interface QualFingerprint {
  persona_id: string;
  expected_features?: Record<string, QualFingerprintFeature>;
  [key: string]: unknown;
}

export interface QualPersonas {
  personas?: QualPersona[];
  fingerprints?: {
    fingerprints?: QualFingerprint[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface QualResearchSegment {
  label: string;
  count: number;
}

export interface QualResearchDesignContext {
  pre_assigned_segments?: QualResearchSegment[];
  total_with_segment?: number;
  stakeholder_types?: Record<string, number>;
  focal_molecule_detected?: string;
}

export interface QualContextV2 {
  research_design_context?: QualResearchDesignContext;
  [key: string]: unknown;
}

export type SegmentationStage =
  | 'upload'
  | 'pipeline'
  | 'eda'
  | 'strategy'
  | 'features'
  | 'segmenting'
  | 'profiling'
  | 'complete'
  | 'segment_error';

export type MessagePart =
  | { type: 'text'; value: string }
  | { type: 'chart'; value: string }
  | { type: 'sql'; value: string };

export type QualMode = 'data_driven' | 'qual_informed' | 'qual_primary';

export type PersonaBasis = 'research_segments' | 'theme_derived';

export interface PersonaExerciseConsensus {
  avg_regimen_allocation?: Record<string, number>;
  tolerability_consensus?: { most_tolerable?: string };
}

export interface QualPersona {
  persona_id: string;
  label: string;
  description?: string;
  size?: number;
  transcript_ids?: string[];
  defining_themes?: string[];
  estimated_pct?: number;
  estimated_hcp_count?: number;
  archetype_affinity?: string;
  theme_score_ranges?: Record<string, { min: number; max: number }>;
  sentiment_signature?: Record<string, number>;
  exercise_consensus?: PersonaExerciseConsensus;
}

export interface QualPersonasData {
  eda_personas?: QualPersona[];
  personas: QualPersona[];
  total_coverage_pct?: number;
  fingerprints?: { fingerprints?: QualFingerprint[] };
  [key: string]: unknown;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  type?: ChatResponse['type'];
  sqlQuery?: string | null;
  dataMarkdown?: string | null;
  /** Ordered arrival log used for streaming — render this instead of content/dataMarkdown when present */
  parts?: MessagePart[];
}

interface StatusUpdate {
  log?: unknown[];
  message?: string;
  progress_pct?: number;
  current_stage?: SegmentationStage;
  current_step?: string;
}

interface SessionState {
  // Session
  sessionId: string | null;
  stage: SegmentationStage;
  activePhaseIdx: number;

  // Data
  eda: EDAResponse | null;
  features: unknown;
  segments: unknown;
  profiles: ProfilesResponse | null;
  strategies: Strategy[];
  selectedStrategy: string | null;
  categoryWeights: Record<string, number>;
  featureSelection: FeatureSelection | null;

  // Status / progress
  statusLog: string[];
  currentMessage: string;
  progressPct: number;
  isProcessing: boolean;

  // Brand configuration
  brandConfig: Record<string, unknown> | null;
  brandConfigVersion: number;
  staleStages: string[];
  configuredThisSession: boolean;
  volumeFilterPreview: Record<string, unknown> | null;

  // Qual
  qualThemes: QualTheme[];
  qualSources: string[];

  // Qual v2
  qualFiles: unknown[]; // server file listing (before processing)
  qualTranscripts: unknown[]; // processed transcript profiles
  edaPersonas: QualPersonasData | null; // personas derived from EDA insights alone
  qualPersonas: QualPersonasData | null; // personas + fingerprints + prevalence
  qualMode: QualMode; // segmentation mode
  qualAlignment: QualAlignment | null; // objective-qual alignment result with gates
  qualContextV2: QualContextV2 | null; // consolidated context (includes exercise_summary)
  qualOverlay: unknown; // sentiment-share matrix + exercise vs claims
  qualPostSeg: unknown; // post-segmentation qual data + archetype bridge
  qualGuide: unknown; // parsed discussion guide structure
  qualResearchDesign: unknown; // pre-assigned segments, focal brand detection
  qualProcessing: boolean; // true during batch processing
  appliedObjectiveText: string | null;
  personaBasis: PersonaBasis | null;
  qualSettingsLoading: boolean;

  // Chart Audit
  chartAuditContext: ChartAuditContext | null;
  chartAuditOverlays: ChartAuditOverlaysResponse | null;
  chartAuditAvailable: boolean; // true while loading any qual-related settings (e.g. themes, sources)

  // Context
  userContextNotes: string[];

  // Chat
  chatMessages: ChatMessage[];
  claimsChatSessionId: string | null;

  // Segment pages (persisted dashboard page IDs per segment index)
  segmentPageIds: Record<number, number>;
  // Stable run identity shared across all segment saves in this session.
  // Generated on first save, reused for subsequent individual saves.
  saveRunId: string | null;
  saveRunTimestamp: string | null;

  // Project metadata
  projectBrand: string;
  projectTitle: string;
  dataSourceName: string;

  // Actions
  setSessionId: (id: string | null) => void;
  setStage: (stage: SegmentationStage) => void;
  setEDA: (eda: EDAResponse | null) => void;
  setFeatures: (features: unknown) => void;
  setSegments: (segments: unknown) => void;
  setProfiles: (profiles: ProfilesResponse | null) => void;
  setStrategies: (strategies: Strategy[]) => void;
  setSelectedStrategy: (key: string | null) => void;
  setCategoryWeights: (w: Record<string, number>) => void;
  setFeatureSelection: (fs: FeatureSelection | null) => void;
  setQualThemes: (themes: QualTheme[]) => void;
  setQualSources: (sources: string[]) => void;
  setQualFiles: (files: unknown[]) => void;
  setQualTranscripts: (transcripts: unknown[]) => void;
  setEdaPersonas: (personas: QualPersonasData | null) => void;
  setQualPersonas: (personas: QualPersonasData | null) => void;
  setQualMode: (mode: QualMode) => void;
  setQualAlignment: (alignment: QualAlignment | null) => void;
  setQualContextV2: (ctx: QualContextV2 | null) => void;
  setQualOverlay: (overlay: unknown) => void;
  setQualPostSeg: (data: unknown) => void;
  setQualGuide: (guide: unknown) => void;
  setQualResearchDesign: (rd: unknown) => void;
  setQualSettingsLoading: (flag: boolean) => void;
  setQualProcessing: (flag: boolean) => void;
  setPersonaBasis: (basis: PersonaBasis) => void;
  setAppliedObjectiveText: (text: string | null) => void;
  setChartAuditContext: (ctx: ChartAuditContext | null) => void;
  setChartAuditOverlays: (overlays: ChartAuditOverlaysResponse | null) => void;
  setChartAuditAvailable: (flag: boolean) => void;
  clearQualState: () => void;
  setBrandConfig: (config: Record<string, unknown> | null) => void;
  setBrandConfigVersion: (version: number) => void;
  incrementBrandConfigVersion: () => void;
  setStaleStages: (stages: string[]) => void;
  setConfiguredThisSession: (v: boolean) => void;
  setVolumeFilterPreview: (preview: Record<string, unknown> | null) => void;
  updateStatus: (status: StatusUpdate) => void;
  addChatMessage: (msg: ChatMessage) => void;
  updateLastChatMessage: (updater: (msg: ChatMessage) => ChatMessage) => void;
  setClaimsChatSessionId: (id: string | null) => void;
  addContextNote: (note: string) => void;
  resetChat: () => void;
  setSegmentPageIds: (mapping: Record<number, number>) => void;
  setSaveRunId: (id: string, timestamp: string) => void;
  setProjectBrand: (brand: string) => void;
  setProjectTitle: (title: string) => void;
  setDataSourceName: (name: string) => void;
  setActivePhaseIdx: (idx: number) => void;
  /** Reset all session state to initial values. Preserves strategies (loaded once) and project metadata (user prefs). */
  resetSession: () => void;
}

const useSessionStore = create<SessionState>((set) => ({
  // Session
  sessionId: null,
  stage: 'upload',
  activePhaseIdx: 0,

  // Data
  eda: null,
  features: null,
  segments: null,
  profiles: null,
  strategies: [],
  selectedStrategy: null,
  categoryWeights: {},
  featureSelection: null,

  // Status / progress
  statusLog: [],
  currentMessage: '',
  progressPct: 0,
  isProcessing: false,

  // Brand configuration
  brandConfig: null,
  brandConfigVersion: 0,
  staleStages: [],
  configuredThisSession: false,
  volumeFilterPreview: null,

  // Qual
  qualThemes: [],
  qualSources: [],

  // Qual v2
  qualSettingsLoading: false, // true while loading any qual-related settings (e.g. themes, sources)
  qualFiles: [], // server file listing (before processing)
  qualTranscripts: [], // processed transcript profiles
  qualPersonas: null, // personas + fingerprints + prevalence
  edaPersonas: null, // personas derived from EDA insights alone
  qualMode: 'data_driven', // segmentation mode
  qualAlignment: null, // objective-qual alignment result with gates
  qualContextV2: null, // consolidated context (includes exercise_summary)
  qualOverlay: null, // sentiment-share matrix + exercise vs claims
  qualPostSeg: null, // post-segmentation qual data + archetype bridge
  qualGuide: null, // parsed discussion guide structure
  qualResearchDesign: null, // pre-assigned segments, focal brand detection
  qualProcessing: false, // true during batch processing
  personaBasis: null,
  appliedObjectiveText: null, // persists the NL text after applying a qual suggestion

  // Chart Audit
  chartAuditContext: null,
  chartAuditOverlays: null,
  chartAuditAvailable: false,

  // Context
  userContextNotes: [],

  // Chat
  chatMessages: [],
  claimsChatSessionId: null,

  // Segment pages
  segmentPageIds: {},
  saveRunId: null,
  saveRunTimestamp: null,

  // Project metadata
  projectBrand: (() => {
    try {
      return localStorage.getItem('seg_projectBrand') || 'Tremfya';
    } catch {
      return 'Tremfya';
    }
  })(),
  projectTitle: (() => {
    const DEFAULT_TITLE = 'Hackathon 2026';
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem('seg_projectTitle') : null;
      return saved && saved.trim().length > 0 ? saved : DEFAULT_TITLE;
    } catch {
      return DEFAULT_TITLE;
    }
  })(),
  dataSourceName: '',

  // Actions
  setSessionId: (id) => set({ sessionId: id }),
  setStage: (stage) => set({ stage }),
  setEDA: (eda) => set({ eda }),
  setFeatures: (features) => set({ features }),
  setSegments: (segments) => set({ segments }),
  setProfiles: (profiles) => set({ profiles }),
  setStrategies: (strategies) => set({ strategies }),
  setSelectedStrategy: (key) => set({ selectedStrategy: key }),
  setCategoryWeights: (w) => set({ categoryWeights: w }),
  setFeatureSelection: (fs) => set({ featureSelection: fs }),
  setQualThemes: (themes) => set({ qualThemes: themes }),
  setQualSources: (sources) => set({ qualSources: sources }),
  setBrandConfigVersion: (version) => set({ brandConfigVersion: version }),

  // Qual v2 setters
  setQualFiles: (files) => set({ qualFiles: files }),
  setQualTranscripts: (transcripts) => set({ qualTranscripts: transcripts }),
  setEdaPersonas: (personas) => set({ edaPersonas: personas }),
  setQualPersonas: (personas) => set({ qualPersonas: personas }),
  setQualMode: (mode) => set({ qualMode: mode }),
  setQualAlignment: (alignment) => set({ qualAlignment: alignment }),
  setQualContextV2: (ctx) => set({ qualContextV2: ctx }),
  setQualOverlay: (overlay) => set({ qualOverlay: overlay }),
  setQualPostSeg: (data) => set({ qualPostSeg: data }),
  setQualGuide: (guide) => set({ qualGuide: guide }),
  setQualResearchDesign: (rd) => set({ qualResearchDesign: rd }),
  setQualProcessing: (flag) => set({ qualProcessing: flag }),
  setPersonaBasis: (basis) => set({ personaBasis: basis }),
  setAppliedObjectiveText: (text) => set({ appliedObjectiveText: text }),
  setChartAuditContext: (ctx) => set({ chartAuditContext: ctx }),
  setChartAuditOverlays: (overlays) => set({ chartAuditOverlays: overlays }),
  setChartAuditAvailable: (flag) => set({ chartAuditAvailable: flag }),
  clearQualState: () =>
    set({
      qualFiles: [],
      qualTranscripts: [],
      qualPersonas: null,
      qualMode: 'data_driven',
      qualAlignment: null,
      qualContextV2: null,
      qualOverlay: null,
      qualPostSeg: null,
      qualGuide: null,
      qualResearchDesign: null,
      qualProcessing: false,
      appliedObjectiveText: null,
      qualThemes: [],
      qualSources: [],
      qualSettingsLoading: false,
    }),
  setQualSettingsLoading: (flag) => set({ qualSettingsLoading: flag }),

  updateStatus: (status) =>
    set({
      statusLog: (status.log ?? []).map((e) =>
        typeof e === 'string' ? e : ((e as { message?: string })?.message ?? String(e))
      ),
      currentMessage: status.message ?? '',
      progressPct: status.progress_pct ?? 0,
      isProcessing: (status.progress_pct ?? 0) < 100,
    }),

  addChatMessage: (msg) =>
    set((s) => ({
      chatMessages: [...s.chatMessages, msg],
    })),

  updateLastChatMessage: (updater) =>
    set((s) => {
      const messages = [...s.chatMessages];
      if (messages.length === 0) return s;
      const last = messages[messages.length - 1];
      messages[messages.length - 1] = updater(last);
      return { chatMessages: messages };
    }),

  setClaimsChatSessionId: (id) => set({ claimsChatSessionId: id }),

  addContextNote: (note) =>
    set((s) => ({
      userContextNotes: [...s.userContextNotes, note],
    })),

  resetChat: () => set({ chatMessages: [], userContextNotes: [], claimsChatSessionId: null }),

  setSegmentPageIds: (mapping) => set({ segmentPageIds: mapping }),
  setSaveRunId: (id, timestamp) => set({ saveRunId: id, saveRunTimestamp: timestamp }),
  setProjectBrand: (brand) => {
    set({ projectBrand: brand });
    try {
      localStorage.setItem('seg_projectBrand', brand);
    } catch {}
  },
  setProjectTitle: (title) => {
    set({ projectTitle: title });
    try {
      localStorage.setItem('seg_projectTitle', title);
    } catch {}
  },
  setDataSourceName: (name) => set({ dataSourceName: name }),
  setActivePhaseIdx: (idx) => set({ activePhaseIdx: idx }),
  setBrandConfig: (config) => set({ brandConfig: config }),
  incrementBrandConfigVersion: () => set((s) => ({ brandConfigVersion: s.brandConfigVersion + 1 })),
  setStaleStages: (stages) => set({ staleStages: stages }),
  setConfiguredThisSession: (v) => set({ configuredThisSession: v }),
  setVolumeFilterPreview: (preview) => set({ volumeFilterPreview: preview }),
  resetSession: () =>
    set({
      // Session
      sessionId: null,
      stage: 'upload',
      activePhaseIdx: 0,

      // Data
      eda: null,
      features: null,
      segments: null,
      profiles: null,
      selectedStrategy: null,
      categoryWeights: {},
      featureSelection: null,

      // Status / progress
      statusLog: [],
      currentMessage: '',
      progressPct: 0,
      isProcessing: false,

      // Brand configuration
      brandConfig: null,
      brandConfigVersion: 0,
      staleStages: [],
      configuredThisSession: false,
      volumeFilterPreview: null,

      // Qual
      qualThemes: [],
      qualSources: [],
      qualFiles: [],
      qualTranscripts: [],
      qualPersonas: null,
      qualMode: 'data_driven',
      qualAlignment: null,
      qualContextV2: null,
      qualOverlay: null,
      qualPostSeg: null,
      qualGuide: null,
      qualResearchDesign: null,
      qualProcessing: false,
      personaBasis: null,
      appliedObjectiveText: null,

      // Chart Audit
      chartAuditContext: null,
      chartAuditOverlays: null,
      chartAuditAvailable: false,

      // Chat
      chatMessages: [],
      claimsChatSessionId: null,
      userContextNotes: [],

      // Dashboard
      segmentPageIds: {},
      saveRunId: null,
      saveRunTimestamp: null,
      dataSourceName: '',

      // Note: strategies, projectBrand, projectTitle are intentionally preserved
    }),
}));

export default useSessionStore;
